import { z } from 'zod'

const orderItemChatReplySchema = z
  .object({
    message_id: z.string().optional(),
    name: z.string().optional(),
    sender_name: z.string().optional(),
    message: z.string().optional(),
    body: z.string().optional(),
    text: z.string().optional(),
    is_sender: z.boolean().optional(),
    timestamp: z.string().optional(),
    created_at: z.string().optional(),
    sent_at: z.string().optional(),
    is_buyer: z.boolean().optional(),
    phone_number: z.string().optional(),
  })
  .passthrough()

/** Wire format of `OrderItemChatMessage` from algo-pos-be. */
export const orderItemChatMessageSchema = orderItemChatReplySchema.extend({
  reply: orderItemChatReplySchema.optional(),
})

export interface ChatHistoryReply {
  senderName: string
  body: string
}

export interface ChatHistoryMessage {
  id: string
  senderName: string
  body: string
  sentAt: string | null
  reply: ChatHistoryReply | null
  /** Buy-intent message that triggered the order (last in slice). */
  isOrigin: boolean
  /** Standalone bubble that the origin message quotes (broadcast / replied-to). */
  isQuotedSource: boolean
  isBuyer: boolean | null
}

function readText(...values: Array<string | undefined | null>) {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return ''
}

function normalizeChatHistoryRaw(raw: unknown) {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as unknown
    } catch {
      return raw
    }
  }
  return raw
}

function parseReply(
  raw: z.infer<typeof orderItemChatMessageSchema> | undefined,
): ChatHistoryReply | null {
  if (!raw) return null
  const body = readText(raw.message, raw.body, raw.text)
  if (!body) return null
  return {
    senderName: readText(raw.name, raw.sender_name) || 'Pengirim',
    body,
  }
}

function parseEntry(raw: unknown, index: number): ChatHistoryMessage | null {
  const parsed = orderItemChatMessageSchema.safeParse(raw)
  if (!parsed.success) return null

  const entry = parsed.data
  const body = readText(entry.message, entry.body, entry.text)
  if (!body) return null

  return {
    id: readText(entry.message_id) || `${index}-${entry.timestamp ?? entry.created_at ?? index}`,
    senderName: readText(entry.name, entry.sender_name) || 'Pengirim',
    body,
    sentAt: entry.timestamp ?? entry.created_at ?? entry.sent_at ?? null,
    reply: parseReply(entry.reply),
    isOrigin: false,
    isQuotedSource: false,
    isBuyer: entry.is_buyer ?? null,
  }
}

function normalizeChatText(value: string) {
  return value.trim().replace(/\s+/g, ' ')
}

function messageMatchesReply(message: ChatHistoryMessage, reply: ChatHistoryReply) {
  const senderMatch =
    message.senderName.trim().toLowerCase() === reply.senderName.trim().toLowerCase()
  if (!senderMatch) return false

  const messageBody = normalizeChatText(message.body)
  const replyBody = normalizeChatText(reply.body)
  if (!replyBody) return false

  return (
    messageBody === replyBody ||
    messageBody.includes(replyBody) ||
    replyBody.includes(messageBody)
  )
}

/** Last message = origin; match the broadcast it quotes for pink highlight. */
function applyChatHistoryFlags(messages: ChatHistoryMessage[]): ChatHistoryMessage[] {
  const startIndex = Math.max(0, messages.length - 11)
  const slice = messages.slice(startIndex)

  const flagged = slice.map((message, index) => ({
    ...message,
    isOrigin: index === slice.length - 1,
    isQuotedSource: false,
  }))

  const origin = flagged[flagged.length - 1]
  if (!origin?.reply) return flagged

  let quotedIndex = -1
  for (let index = flagged.length - 2; index >= 0; index -= 1) {
    const candidate = flagged[index]
    if (candidate && messageMatchesReply(candidate, origin.reply)) {
      quotedIndex = index
      break
    }
  }

  if (quotedIndex < 0) return flagged

  return flagged.map((message, index) =>
    index === quotedIndex ? { ...message, isQuotedSource: true } : message,
  )
}

/** Normalize `order_items[].chat_history` from algo-pos-be. Last message = origin buy-intent. */
export function parseChatHistory(raw: unknown): ChatHistoryMessage[] {
  const normalized = normalizeChatHistoryRaw(raw)
  if (!Array.isArray(normalized)) return []

  const messages = normalized
    .map((entry, index) => parseEntry(entry, index))
    .filter((entry): entry is ChatHistoryMessage => entry != null)

  if (messages.length === 0) return messages

  return applyChatHistoryFlags(messages)
}

/** Pick chat payload off an order item, supporting snake_case and camelCase. */
export function extractOrderItemChatRaw(payload: {
  chat_history?: unknown
  chatHistory?: unknown
  product?: Record<string, unknown> | null
}) {
  const product = payload.product
  return (
    payload.chat_history ??
    payload.chatHistory ??
    product?.chat_history ??
    product?.chatHistory ??
    null
  )
}

/** True when chat exists on the wire or parses into at least one bubble. */
export function orderItemHasChatData(raw: unknown) {
  if (parseChatHistory(raw).length > 0) return true
  const normalized = normalizeChatHistoryRaw(raw)
  return Array.isArray(normalized) && normalized.length > 0
}

export function isBuyerChatSender(
  senderName: string,
  buyerName: string,
  buyerPhone: string | null,
) {
  const sender = senderName.trim().toLowerCase()
  if (!sender) return false

  const buyer = buyerName.trim().toLowerCase()
  if (buyer) {
    const buyerToken = buyer.split(/\s+/)[0]
    if (buyerToken && sender.includes(buyerToken)) return true
  }

  const phoneDigits = buyerPhone?.replace(/\D/g, '') ?? ''
  const senderDigits = sender.replace(/\D/g, '')
  if (phoneDigits.length >= 4 && senderDigits.includes(phoneDigits.slice(-4))) return true

  return false
}

const chatDate = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

export function formatChatTimestamp(iso: string | null) {
  if (!iso) return '—'
  const parsed = Date.parse(iso)
  if (Number.isNaN(parsed)) return iso
  return chatDate.format(new Date(parsed))
}

export type ChatBubbleVariant = 'default' | 'quoted-source' | 'origin'

/** Bubble variant keyed by message role in the thread. */
export function chatBubbleVariant(message: ChatHistoryMessage): ChatBubbleVariant {
  if (message.isOrigin) return 'origin'
  if (message.isQuotedSource) return 'quoted-source'
  return 'default'
}

const bubbleShell: Record<ChatBubbleVariant, string> = {
  default: 'bg-slate-100 text-ink',
  'quoted-source': 'bg-brand-50 text-ink',
  origin: 'bg-brand-50 text-ink ring-1 ring-brand-600',
}

/** Shared bubble + quote classes so every message with the same id variant looks identical. */
export function chatBubbleShellClass(message: ChatHistoryMessage) {
  return bubbleShell[chatBubbleVariant(message)]
}

export function chatQuoteBlockClass(_message: ChatHistoryMessage) {
  return 'mb-2 rounded-lg bg-white px-2.5 py-2 text-xs leading-relaxed ring-1 ring-slate-200'
}

export const chatMessageBodyClass = 'whitespace-pre-wrap font-normal'

export function chatQuoteSenderClass(message: ChatHistoryMessage) {
  return message.reply ? 'font-bold text-muted' : 'font-bold text-ink'
}

export function chatQuoteBodyClass(message: ChatHistoryMessage) {
  return message.reply
    ? 'text-ink mt-0.5 font-normal whitespace-pre-wrap'
    : 'text-muted mt-0.5 font-normal whitespace-pre-wrap'
}

export const chatMetaNameClass = 'text-ink font-bold'

export const chatMetaTimeClass = 'text-muted font-normal'
